<template>
  <div ref="compare" class="products-compare">
    <div
      class="compare-header-wrapper"
      :class="{ 'sticky-header': stickyHeader }"
    >
      <div class="scroll-wrap slider_arrows">
        <div
          ref="header"
          class="compare-header-scroll"
          data-sync-scroll="header"
          @scroll="(e) => onScroll(e, 'header')"
        >
          <div class="compare-header">
            <div class="compare-menu sticky-left">
              <div class="show-menu">
                <UiPCheckbox
                  v-if="isMobile"
                  :value="onlyDifferent"
                  label="Только различия"
                  label-class="body-m-regular"
                  @change="onlyDifferent = !onlyDifferent"
                />
                <template v-else>
                  <div class="show-text body-m-semibold">Показывать</div>
                  <ui-p-radio
                    :value="onlyDifferent"
                    :assigned-value="false"
                    text="Все характеристики"
                    class="radio"
                    @change="onlyDifferent = !onlyDifferent"
                  />
                  <ui-p-radio
                    :value="onlyDifferent"
                    :assigned-value="true"
                    text="Только различия"
                    class="radio"
                    @change="onlyDifferent = !onlyDifferent"
                  />
                </template>
              </div>
              <nuxt-link
                class="category-link body-m-semibold"
                :to="`/${category?.slug}/`"
              >
                Добавить товар
              </nuxt-link>
            </div>
            <div
              v-for="(item, index) in products"
              :key="item.id"
              class="product"
            >
              <ui-responsive-image
                class="product_image"
                :src="item.image || '/static/images/no-img.svg'"
                :alt="item.product_type + ' ' + item?.name"
                :title="item.product_type + ' ' + item?.name"
                :mobile-sizes="{ width: 96, height: 96 }"
                :desktop-sizes="{ width: 132, height: 132 }"
                @click.prevent="clickNative(item?.slug)"
              />
              <div class="product_icons">
                <div
                  :class="['favorite', { active: favorite(item.id) }]"
                  @click="toggleFavorite(item.id, item.category.id)"
                />
                <div class="delete" @click="deleteItem(index)" />
              </div>
              <div
                class="product_name body-l-medium"
                @click.prevent="$router.push(`/${item?.slug}/`)"
              >
                {{ item.product_type }} {{ item?.name }}
              </div>
              <div
                class="product_price price-s"
                @click.prevent="$router.push(`/${item?.slug}/`)"
              >
                <template v-if="item.price_info && item.offer_count">
                  от
                  <ui-currency :value="item.price_info.min" />
                </template>
                <template v-else> Нет в продаже</template>
              </div>
            </div>
          </div>
        </div>
        <template v-if="isArrows.show">
          <ui-ui-button-ico
            v-show="isArrows.left"
            mode="button"
            icon="fill-left"
            type="primary"
            class="left"
            size="large"
            size-ico="20"
            fill="white"
            @click="slideGo(1)"
          />
          <ui-ui-button-ico
            v-show="isArrows.right"
            mode="button"
            icon="fill-right"
            type="primary"
            class="right"
            size="large"
            size-ico="20"
            fill="white"
            @click="slideGo(0)"
          />
        </template>
      </div>
    </div>
    <div v-if="specifications" class="specifications-list">
      <ui-slide-down
        v-for="(item, index) in specifications"
        v-show="
          item.specifications.length &&
          (!onlyDifferent || groupDifferent[index])
        "
        :key="index"
        class="compare-group"
        open
        @change="onSlideDownChange"
      >
        <template #header="{ visible }">
          <h3 class="group-header upper-text heading-m">
            {{ item.group }}
            <div class="is-body-opened" :class="{ closed: !visible }" />
          </h3>
        </template>
        <template #default>
          <div
            class="group-spec"
            :data-sync-scroll="index"
            @scroll="(e) => onScroll(e, index)"
            @mouseleave="setHoverStyle(null)"
          >
            <div
              v-for="(spec, indx) in item.specifications"
              v-show="!onlyDifferent || spec.different"
              :key="`${index}-${indx}`"
              class="group-spec_row"
              @mouseenter="setHoverStyle"
              @click.stop="closeHint"
            >
              <div class="spec-title sticky-left body-l-medium">
                {{ spec.title }}
                <template v-if="spec?.description">
                  <div
                    class="hint desktop"
                    :class="{ active: hint && `${index}-${indx}` === hint.key }"
                    @mouseenter="
                      (e) =>
                        onHintClick(e, `${index}-${indx}`, spec.description)
                    "
                  />
                  <div
                    class="hint tablet"
                    :class="{ active: hint && `${index}-${indx}` === hint.key }"
                    @click.stop="
                      (e) =>
                        onHintClick(e, `${index}-${indx}`, spec.description)
                    "
                  />
                </template>
              </div>
              <div
                v-for="(val, indxVal) in spec.value"
                :key="`${index}-${indx}-${indxVal}`"
                class="spec-value body-l-medium"
                :class="{ 'not-found': !val }"
              >
                {{ val ? val : 'Нет данных' }}
              </div>
            </div>
          </div>
        </template>
      </ui-slide-down>
    </div>
    <div class="hover-line" :style="hoverStyle" />
    <div
      v-if="hint"
      class="hint-body"
      :style="hint.style"
      @mouseleave="closeHint()"
    >
      <div class="corner" :style="hint.cornerStyle" />
      <perfect-scrollbar
        v-if="hint?.description"
        :options="{ wheelPropagation: false }"
      >
        <RenderHTML
          :seotext="hint.description"
          class-wrapper="block-text-description"
        />
      </perfect-scrollbar>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFavoriteStore } from '~/stores/favorite';
import type { ModelCategory, ResponseModel } from '@/interfaces/api';
import type {
  groupManySpecifications,
  styleHover,
  HintInterface,
  Arrows,
} from '@/interfaces/app';
import { useMediaQueryStore } from '~/stores/mediaQuery';
import RenderHTML from '~/components/ui/RenderHTML.vue';

const emit = defineEmits(['delete', 'click']);
const props = defineProps({
  products: {
    type: Array as () => ResponseModel[],
    required: true,
  },
  specifications: Array as () => groupManySpecifications[],
  category: {
    type: Object as () => ModelCategory,
    required: true,
  },
});
const mediaStore = useMediaQueryStore();
const favoriteStore = useFavoriteStore();
const header = ref<HTMLElement>();
const compare = ref<HTMLElement>();
const compareValueHtml = computed(() => compare.value as HTMLElement);
let scrolling = 0;
const hoverStyle = ref<styleHover>();
const stickyHeader = ref<boolean>(false);
const onlyDifferent = ref<boolean>(false);
const isMobile = computed(
  () =>
    mediaStore.isMobile || mediaStore.isEs || mediaStore.isSm || mediaStore.isMd
);
const hint = ref<HintInterface>();
const isArrows = ref<Arrows>({
  show: false,
  left: false,
  right: true,
});
const router = useRouter();

watch(
  () => props.products,
  () => nextTick().then(() => checkArrows())
);

watch(
  () => props.specifications,
  () => nextTick().then(() => checkArrows())
);

const groupDifferent = computed({
  get: () =>
    props.specifications?.map((el) =>
      el.specifications.some((el2) => el2.different)
    ) || [],
  set: () => {},
});

function onScroll(e: KeyboardEvent, where: string | number) {
  const target = e.target as HTMLElement;
  const newVal = target.scrollLeft;
  if (scrolling !== newVal && header.value) {
    const element = header.value as HTMLElement;
    scrolling = newVal;
    isArrows.value = {
      show: true,
      left: newVal > 0,
      right: newVal + header.value?.offsetWidth < element.scrollWidth,
    };
    compareValueHtml.value
      ?.querySelectorAll(
        `[data-sync-scroll]:not([data-sync-scroll="${where}"])`
      )
      .forEach((el) => (el.scrollLeft = newVal));
  }
}

function setHoverStyle(e: MouseEvent | null): void {
  if (e && compare.value) {
    const target = e.target as HTMLElement;
    const { height, top } = target.getBoundingClientRect();
    hoverStyle.value = {
      top: top - compare.value?.getBoundingClientRect().top + 'px',
      height: height + 'px',
    };
  } else hoverStyle.value = undefined;
}

function deleteItem(index: number) {
  if (props.specifications) emit('delete', index);
  nextTick().then(() => checkArrows());
}

function onResize() {
  if (compareValueHtml.value) {
    compareValueHtml.value.style.height = '';
    onSticky();
  }
}

function onSticky(): void {
  if (
    compareValueHtml.value &&
    compareValueHtml.value.getBoundingClientRect().top <= 0
  ) {
    compareValueHtml.value.style.height = '';
    compareValueHtml.value.style.height = compare.value?.offsetHeight + 'px';
    stickyHeader.value = true;
  } else if (stickyHeader.value) {
    compareValueHtml.value.style.height = '';
    stickyHeader.value = false;
  }
}

function onHintClick(e: MouseEvent, key: string, description: string): void {
  if (e && compare.value && hint.value?.key !== key) {
    const target = e.target as HTMLElement;
    const { bottom, left } = target.getBoundingClientRect();
    const { top: topC, left: leftC } =
      compareValueHtml.value.getBoundingClientRect();
    hint.value = {
      style: `top: ${bottom - topC + 12}px`,
      cornerStyle: `left: ${left - leftC}px`,
      key,
      description,
    };
  } else {
    closeHint();
  }
}

function closeHint(): void {
  hint.value = undefined;
}

function clickNative(value: string) {
  emit('click');
  router.push(`/${value}/`);
}

function checkArrows() {
  if (header.value) {
    const elementHeader = header.value as HTMLElement;
    isArrows.value.show =
      elementHeader.scrollWidth !== elementHeader?.offsetWidth;
    isArrows.value.left = !isArrows.value.right;
  }
}

function slideGo(direction: 0 | 1) {
  if (header.value && isArrows.value.show) {
    const element = header.value as HTMLElement;
    const elementSelector = element.querySelector('.product') as HTMLElement;
    const width = elementSelector?.offsetWidth;

    const elementScrollLeft = element.scrollLeft;

    const slideNum: number = Math.ceil(elementScrollLeft / width);
    const slideNext: number = (slideNum + (-1) ** direction) * width;

    const newScrollLeft = slideNext;
    if (compare.value && scrolling !== slideNext * width) {
      scrolling = newScrollLeft;
      isArrows.value = {
        show: true,
        left: newScrollLeft > 0,
        right: newScrollLeft + header.value?.offsetWidth < element.scrollWidth,
      };
      const compareValue =
        compareValueHtml.value.querySelectorAll('[data-sync-scroll]');
      compareValue.forEach((el) => (el.scrollLeft = newScrollLeft));
    }
  }
}

onMounted(() => {
  window.addEventListener('scroll', onSticky);
  window.addEventListener('scroll', closeHint);
  window.addEventListener('resize', onResize);
  onSticky();
  onResize();
  checkArrows();
});

onUnmounted(() => {
  window.removeEventListener('scroll', onSticky);
  window.removeEventListener('scroll', closeHint);
  window.removeEventListener('resize', onResize);
});

function favorite(id: number) {
  return Boolean(props.products && favoriteStore.ids.includes(id));
}

const toggleFavorite = (id: number, catId: number | string) => {
  const deleteCard = favorite(id);
  if (props.products)
    favoriteStore.toggleFavorite({
      id,
      catId: Number(catId),
      deleteCard,
    });
};

function onSlideDownChange() {
  closeHint();
  if (compareValueHtml.value) compareValueHtml.value.style.height = '';
}
</script>
<style lang="scss" src="./CompareProductsScss.scss" scoped />
